import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, Trash2, PenLine, Info, Archive, ArchiveRestore } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  VariantDialog,
  DeleteConfirmDialog,
  BaseDialog,
  DetailsDialog,
  AddOns,
  ToppingsDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import {
  deletePizzaById,
  clearPizzaMessages,
  updateExistingPizza,
  fetchAllPizzas,
} from "@/redux/slice/menu-manage-slice/pizza-slice";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import hasPermission from "@/hooks/hasPermission";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function PizzaListTable({ pizzas }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { error, requestStatus, deleteLoading, updateLoading } = useSelector(
    (state) => state.menuManage.pizza
  );
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [itemDialgo, setItemDialog] = useState(null);
  const [variantDetails, setVarianDetails] = useState(false);
  const [pizzaBase, setPizzaBase] = useState(false);
  const [addOnsDialog, setToppingsDialog] = useState(false);
  const [pizzaAddOns, setPizzaAddOnsDialog] = useState(false);
  const [vodkaDetailsDialog, setVodkaDetailsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
    setItemDialog(null);
  };
  const closeVarintDetails = () => {
    setVarianDetails(false);
    setItemDialog(null);
  };
  const colseIngredients = () => {
    setPizzaBase(false);
    setItemDialog(null);
  };
  const closeToppings = () => {
    setToppingsDialog(false);
    setItemDialog(null);
  };
  const closeAllergen = () => {
    setPizzaAddOnsDialog(false);
    setItemDialog(null);
  };
  const closeVodkaDetails = () => {
    setVodkaDetailsDialog(false);
    setItemDialog(null);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearPizzaMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const res = await dispatch(
        updateExistingPizza({
          id,
          data: {
            ...item,
            status: newStatus,
          },
        })
      );
      if (updateExistingPizza.fulfilled.match(res)) {
        toast.success(`Pizza archived successfully!`);
      } else if (updateExistingPizza.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive pizza.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearPizzaMessages());
      const res = await dispatch(deletePizzaById(id));
      if (deletePizzaById.fulfilled.match(res)) {
        toast.success(`Pizza deleted successfully!`);
      } else if (deletePizzaById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete pizza.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("pizzas", id);
      if (res.status === "success") {
        toast.success(res.message || "Pizza status updated successfully");
        dispatch(fetchAllPizzas(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <Table className="w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px]">Item No</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Base</TableHead>
          <TableHead>Size & Price</TableHead>
          <TableHead>Toppings</TableHead>
          <TableHead>Add-ons</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pizzas && pizzas?.length > 0 ? (
          pizzas.map((item, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">
                {item?.item_no || "N/A"}
              </TableCell>
              <TableCell>{item?.name}</TableCell>
              <TableCell>
                <Eye
                  onClick={() => {
                    setPizzaBase(true);
                    setItemDialog(item);
                  }}
                  size={18}
                />
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center cursor-pointer">
                  {item?.variants_count || "0"}
                  <Eye
                    onClick={() => {
                      setVarianDetails(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center cursor-pointer">
                  {item?.toppings_count}
                  <Eye
                    onClick={() => {
                      setToppingsDialog(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center cursor-pointer">
                  {item?.add_ons_count || "0"}
                  <Eye
                    onClick={() => {
                      setPizzaAddOnsDialog(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {item?.status === "active" ? (
                    <>
                      <span className="text-cgreen text-base">Active</span>
                      <Switch
                        checked={true}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "inactive" ? (
                    <>
                      <span className="text-cred text-base">Inactive</span>
                      <Switch
                        checked={false}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "archived" ? (
                    <span className="text-cred text-base">Archived</span>
                  ) : (
                    <span className="text-cred text-base">Draft</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right justify-end flex gap-2 items-center">
                <div className="flex items-center gap-2 h-8">
                  <Info
                    onClick={() => {
                      setVodkaDetailsDialog(true);
                      setItemDialog(item);
                    }}
                    className="text-cwhite3 cursor-pointer"
                    size={18}
                  />

                  {hasPermission("back_office.menu_management.update_pizza") && item?.status !== "archived" && (
                    <Link
                      to={"/backoffice/menu/pizza/update"}
                      state={item}
                      className="cursor-pointer"
                    >
                      <PenLine className="text-cyellow" size={18} />
                    </Link>
                  )}
                  {hasPermission("back_office.menu_management.delete_pizza") && item?.status !== "archived" && (
                    <span>
                      {item?.status === "draft" ? (
                        <Trash2
                          onClick={() => {
                            setItemDialog(item);
                            setDeleteDialog(true);
                          }}
                          className="text-cred cursor-pointer"
                          size={18}
                        />
                      ) : (
                        <Archive
                          onClick={() => {
                            setArchiveActionType("archive");
                            setItemDialog(item);
                            setArchiveDialog(true);
                          }}
                          className="text-cred cursor-pointer"
                          size={18}
                        />
                      )}
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={8} className="text-center">
              No data available
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      {/* dialog */}
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemDialgo.id, itemDialgo, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Pizza?" : "Restore Pizza?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemDialgo?.name}"?`
            : `Are you sure you want to restore "${itemDialgo?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemDialgo.id);
        }}
        title="Delete Draft Pizza?"
        description={`Are you sure you want to delete the draft pizza "${itemDialgo?.name}"? This action cannot be undone.`}
      />
      <VariantDialog
        teaCoffee={true}
        crust={true}
        items={itemDialgo}
        isOpen={variantDetails}
        onClose={closeVarintDetails}
      />
      <BaseDialog
        items={itemDialgo}
        isOpen={pizzaBase}
        onClose={colseIngredients}
      />
      <ToppingsDialog
        items={itemDialgo}
        isOpen={addOnsDialog}
        onClose={closeToppings}
      />
      <AddOns items={itemDialgo} isOpen={pizzaAddOns} onClose={closeAllergen} />
      <DetailsDialog
        items={itemDialgo}
        isOpen={vodkaDetailsDialog}
        onClose={closeVodkaDetails}
      />
    </Table>
  );
}

export default PizzaListTable;
